/**
 * 链上余额查询服务
 * 使用 ethers.js 查询 Polygon 上的代币余额
 */

import { ethers } from 'ethers';
import { POLYGON_RPC_URL, SUPPORTED_TOKENS } from '@/constants/Config';

// ERC20 标准 ABI（只包含 balanceOf）
const ERC20_ABI = ['function balanceOf(address) view returns (uint256)'];

/**
 * 创建 Polygon Provider
 */
const getProvider = () => {
  try {
    return new ethers.JsonRpcProvider(POLYGON_RPC_URL);
  } catch (error) {
    console.error('❌ [OnChainBalance] 创建 Provider 失败:', error);
    throw error;
  }
};

/**
 * 查询单个代币余额
 * @param walletAddress 钱包地址
 * @param tokenAddress 代币合约地址
 * @param decimals 代币精度
 * @returns 余额（格式化后的数字）
 */
export const getTokenBalance = async (
  walletAddress: string,
  tokenAddress: string,
  decimals: number,
): Promise<number> => {
  try {
    const provider = getProvider();
    const contract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);

    console.log(`🔍 [OnChainBalance] 查询余额: ${walletAddress} - ${tokenAddress}`);

    const balance = await contract.balanceOf(walletAddress);
    const formattedBalance = parseFloat(ethers.formatUnits(balance, decimals));

    console.log(`✅ [OnChainBalance] 余额查询成功: ${formattedBalance}`);

    return formattedBalance;
  } catch (error) {
    console.error(`❌ [OnChainBalance] 查询余额失败:`, error);
    return 0; // 查询失败返回 0
  }
};

/**
 * 查询钱包的 USDC.e 余额
 * @param walletAddress 钱包地址
 * @returns USDC.e 余额
 */
export const getUSDCBalance = async (walletAddress: string): Promise<number> => {
  const { address, decimals } = SUPPORTED_TOKENS.P_USD;
  return getTokenBalance(walletAddress, address, decimals);
};

/**
 * 查询钱包的所有支持代币余额
 * @param walletAddress 钱包地址
 * @returns 总余额（USD 价值）
 */
export const getTotalOnChainBalance = async (walletAddress: string): Promise<number> => {
  try {
    // 目前只支持 USDC.e，未来可以扩展到其他代币
    const usdcBalance = await getUSDCBalance(walletAddress);

    // USDC.e 是稳定币，1 USDC.e = 1 USD
    const totalValue = usdcBalance * 1;

    console.log(`✅ [OnChainBalance] 总余额: $${totalValue.toFixed(2)}`);

    return totalValue;
  } catch (error) {
    console.error('❌ [OnChainBalance] 查询总余额失败:', error);
    return 0;
  }
};

/**
 * 批量查询多个钱包的链上余额
 * @param walletAddresses 钱包地址数组
 * @returns 总余额（USD 价值）
 */
export const getMultiWalletOnChainBalance = async (walletAddresses: string[]): Promise<number> => {
  try {
    console.log(`🔍 [OnChainBalance] 批量查询 ${walletAddresses.length} 个钱包余额`);

    // 并发查询所有钱包
    const balances = await Promise.all(
      walletAddresses.map((address) => getTotalOnChainBalance(address)),
    );

    const totalBalance = balances.reduce((sum, balance) => sum + balance, 0);

    console.log(`✅ [OnChainBalance] 批量查询完成，总余额: $${totalBalance.toFixed(2)}`);

    return totalBalance;
  } catch (error) {
    console.error('❌ [OnChainBalance] 批量查询失败:', error);
    return 0;
  }
};

/**
 * 查询钱包的余额详情（包含各个代币）
 * @param walletAddress 钱包地址
 * @returns 余额详情
 */
export interface TokenBalanceDetail {
  symbol: string;
  balance: number;
  usdValue: number;
}

export const getBalanceDetails = async (walletAddress: string): Promise<TokenBalanceDetail[]> => {
  try {
    const usdcBalance = await getUSDCBalance(walletAddress);

    return [
      {
        symbol: SUPPORTED_TOKENS.USDC_E.symbol,
        balance: usdcBalance,
        usdValue: usdcBalance * 1, // USDC.e = 1 USD
      },
      // 未来可以添加更多代币...
    ];
  } catch (error) {
    console.error('❌ [OnChainBalance] 查询余额详情失败:', error);
    return [];
  }
};
